import { TestTrend } from '../types/trends';

const CACHE_PREFIX = 'trendsCache_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const EXTENDED_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes for stable data
const CACHE_VERSION = 2; // Increment to invalidate all caches

interface CachedData {
  data: TestTrend;
  timestamp: number;
  sessionId: string;
  version?: number;
  etag?: string;
  lastModified?: string;
}

// IndexedDB-based cache for larger datasets
class PersistentTrendsCache {
  private dbName = 'TrendsCache';
  private storeName = 'trends';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    if (this.db) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp');
        }
      };
    });
  }

  async get(key: string): Promise<CachedData | null> {
    await this.init();
    if (!this.db) return null;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);
      
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
    });
  }

  async set(key: string, data: CachedData): Promise<void> {
    await this.init();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put({ key, ...data });
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(): Promise<void> {
    await this.init();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

const persistentCache = new PersistentTrendsCache();

export const TrendsCache = {
  get: (testName: string, dateRange: string, referenceSource: string, sessionId: string): TestTrend | null => {
    try {
      const key = `${CACHE_PREFIX}${sessionId}_${testName}_${dateRange}_${referenceSource}`;
      const cached = sessionStorage.getItem(key);
      
      if (!cached) return null;
      
      const parsedCache: CachedData = JSON.parse(cached);
      
      // Check cache version
      if (!parsedCache.version || parsedCache.version < CACHE_VERSION) {
        sessionStorage.removeItem(key);
        return null;
      }
      
      // Use extended cache duration for older date ranges (unlikely to change)
      const isHistoricalData = dateRange.includes('all_time') || dateRange.includes('5_years') || dateRange.includes('1_year');
      const cacheDuration = isHistoricalData ? EXTENDED_CACHE_DURATION : CACHE_DURATION;
      
      // Check if cache is expired or from different session
      if (
        Date.now() - parsedCache.timestamp > cacheDuration ||
        parsedCache.sessionId !== sessionId
      ) {
        sessionStorage.removeItem(key);
        return null;
      }
      
      return parsedCache.data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  },

  set: (testName: string, data: TestTrend, dateRange: string, referenceSource: string, sessionId: string): void => {
    try {
      const key = `${CACHE_PREFIX}${sessionId}_${testName}_${dateRange}_${referenceSource}`;
      const cacheData: CachedData = {
        data,
        timestamp: Date.now(),
        sessionId,
        version: CACHE_VERSION
      };
      
      sessionStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      // Handle quota exceeded error
      if (error instanceof DOMException && error.code === 22) {
        // Clear old cache entries
        TrendsCache.clearOldEntries();
        // Try again
        try {
          const key = `${CACHE_PREFIX}${sessionId}_${testName}_${dateRange}_${referenceSource}`;
          sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now(), sessionId, version: CACHE_VERSION }));
        } catch {
          console.warn('Failed to cache trends data - storage full');
        }
      }
    }
  },

  clearOldEntries: (): void => {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const cached = sessionStorage.getItem(key);
          if (cached) {
            const parsedCache: CachedData = JSON.parse(cached);
            if (Date.now() - parsedCache.timestamp > CACHE_DURATION) {
              keysToRemove.push(key);
            }
          }
        } catch {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  },

  clearAll: (): void => {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  },

  // Get data from persistent cache (IndexedDB)
  getPersistent: async (testName: string, dateRange: string, referenceSource: string, sessionId: string): Promise<TestTrend | null> => {
    try {
      const key = `${CACHE_PREFIX}${sessionId}_${testName}_${dateRange}_${referenceSource}`;
      const cached = await persistentCache.get(key);
      
      if (!cached) return null;
      
      // Check cache version
      if (!cached.version || cached.version < CACHE_VERSION) {
        return null;
      }
      
      // Use extended cache duration for older date ranges
      const isHistoricalData = dateRange.includes('all_time') || dateRange.includes('5_years') || dateRange.includes('1_year');
      const cacheDuration = isHistoricalData ? EXTENDED_CACHE_DURATION : CACHE_DURATION;
      
      // Check if cache is expired or from different session
      if (
        Date.now() - cached.timestamp > cacheDuration ||
        cached.sessionId !== sessionId
      ) {
        return null;
      }
      
      return cached.data;
    } catch (error) {
      console.error('Error reading from persistent cache:', error);
      return null;
    }
  },

  // Set data in persistent cache
  setPersistent: async (testName: string, data: TestTrend, dateRange: string, referenceSource: string, sessionId: string): Promise<void> => {
    try {
      const key = `${CACHE_PREFIX}${sessionId}_${testName}_${dateRange}_${referenceSource}`;
      const cacheData: CachedData = {
        data,
        timestamp: Date.now(),
        sessionId,
        version: CACHE_VERSION
      };
      
      await persistentCache.set(key, cacheData);
    } catch (error) {
      console.error('Error writing to persistent cache:', error);
    }
  },

  // Clear all persistent cache
  clearPersistent: async (): Promise<void> => {
    try {
      await persistentCache.clear();
    } catch (error) {
      console.error('Error clearing persistent cache:', error);
    }
  },

  // Get cache statistics
  getCacheStats: (): { sessionStorageUsed: number; sessionStorageItems: number } => {
    let used = 0;
    let items = 0;
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        items++;
        const value = sessionStorage.getItem(key);
        if (value) {
          used += key.length + value.length;
        }
      }
    }
    
    return { sessionStorageUsed: used, sessionStorageItems: items };
  }
};